/**
 * React Hooks for Input Sanitization and XSS Protection
 */

import { useMemo, useCallback } from 'react';
import { getSanitizationService, SanitizationProfile } from '@/services/security/sanitization';
import type { SanitizationOptions } from '@/services/security/sanitization';

/**
 * Hook to sanitize HTML content
 */
export function useSanitizedHTML(
  html: string,
  options?: SanitizationOptions
): string {
  const sanitizationService = getSanitizationService();

  return useMemo(() => {
    return sanitizationService.sanitizeHTML(html, options) as string;
  }, [html, options, sanitizationService]);
}

/**
 * Hook to sanitize text input (strips all HTML)
 */
export function useSanitizedText(text: string): string {
  const sanitizationService = getSanitizationService();

  return useMemo(() => {
    return sanitizationService.sanitizeText(text);
  }, [text, sanitizationService]);
}

/**
 * Hook to get sanitization callback function
 */
export function useSanitize() {
  const sanitizationService = getSanitizationService();

  const sanitizeHTML = useCallback(
    (html: string, options?: SanitizationOptions) => {
      return sanitizationService.sanitizeHTML(html, options);
    },
    [sanitizationService]
  );

  const sanitizeText = useCallback(
    (text: string) => {
      return sanitizationService.sanitizeText(text);
    },
    [sanitizationService]
  );

  const sanitizeEmail = useCallback(
    (email: string) => {
      return sanitizationService.sanitizeEmail(email);
    },
    [sanitizationService]
  );

  const sanitizePhone = useCallback(
    (phone: string) => {
      return sanitizationService.sanitizePhone(phone);
    },
    [sanitizationService]
  );

  const sanitizeURL = useCallback(
    (url: string) => {
      return sanitizationService.sanitizeURL(url);
    },
    [sanitizationService]
  );

  const sanitizeObject = useCallback(
    <T extends Record<string, any>>(obj: T, profile?: SanitizationProfile) => {
      return sanitizationService.sanitizeObject(obj, profile);
    },
    [sanitizationService]
  );

  return {
    sanitizeHTML,
    sanitizeText,
    sanitizeEmail,
    sanitizePhone,
    sanitizeURL,
    sanitizeObject,
  };
}

/**
 * Hook for secure form input handling
 */
export function useSecureInput<T extends Record<string, any>>(
  _initialValues: T,
  profile: SanitizationProfile = SanitizationProfile.STRICT
) {
  const sanitizationService = getSanitizationService();

  const sanitizeValue = useCallback(
    (value: any): any => {
      if (typeof value === 'string') {
        return sanitizationService.sanitizeHTML(value, { profile });
      }
      return value;
    },
    [sanitizationService, profile]
  );

  const sanitizeValues = useCallback(
    (values: T): T => {
      return sanitizationService.sanitizeObject(values, profile);
    },
    [sanitizationService, profile]
  );

  return {
    sanitizeValue,
    sanitizeValues,
  };
}
